import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  PageHeader,
  Button,
  Divider,
  Form,
  Input,
  message,
  Upload,
  Select,
  Switch,
  DatePicker,
  Radio, Table, Checkbox, Descriptions,
} from "antd";

import * as userAPI from "../../../api/user";

import {
  CloseOutlined,
  CheckOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  dateToCompactString,
  dateToLongString,
  dateToString,
  getDateTimeString, numberWithCommas,
  onlyDateToString
} from "../../../util/common";
import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";
import localeData from "dayjs/plugin/localeData";
import TextArea from "antd/lib/input/TextArea";
import {FlexRowContainer} from "../../Common/Container.styles";
import * as orderAPI from "../../../api/order";
import {createUser} from "../../../api/user";
import {useDaumPostcodePopup} from "react-daum-postcode";
import DescriptionsItem from "antd/es/descriptions/Item";

dayjs.extend(weekday);
dayjs.extend(localeData);

function UserForm({ isEdit }) {
  const [form] = Form.useForm();

  const navigate = useNavigate();
  const params = useParams();

  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  const fetchUser = () => {
    userAPI
      .getUser({ id: params.id })
      .then((res) => {
        if (res.data.linusCode === 1) {
          setUser({
            ...res.data.data,
            useStartDate: dayjs(res.data.data.useStartDate),
            useEndDate: dayjs(res.data.data.useEndDate),
          });

          setLoading(false);
        } else {
          alert(res.data.data.msg);
        }
      })
      .catch((error) => {
        if (error.response.data.detail) {
          message.error(error.response.data.detail);
        } else if (error.response.status < 500) {
          for (let key of Object.keys(error.response.data)) {
            message.error(`${key}: ${error.response.data[key]}`);
          }
        } else {
          message.error("오류가 발생했습니다.");
        }
      });
  };

  useEffect(() => {
    if (isEdit) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const onFinish = (values) => {
    if (!values?.name?.length || !values?.phone?.length || !values?.email?.length) {
      alert('필수 항목들을 입력해주세요.')
      return
    }

    if (isEdit) {
      userAPI
        .editUser({
          ...user,
          ...values,
        })
        .then((res) => {
          if (res.data.linusCode === 1) {
            message.success("성공적으로 수정되었습니다.");
            navigate(`/user/user`);
          } else {
            message.error(res.data.data.msg);
          }
        })
        .catch((error) => {
          if (error?.response?.data?.data?.msg?.indexOf('gender') !== -1) {
            message.error('성별 형식을 남성/여성 으로 입력해 주세요.')
          } else if (error?.response?.data?.data?.msg?.indexOf('email') !== -1){
            message.error('이메일 형식 또는 중복 여부를 확인해 주세요.')
          } else {
            message.error(error?.response?.data?.data?.msg)
          }

          // if (error.response.data.detail) {
          //   message.error(error.response.data.detail);
          // } else if (error.response.status < 500) {
          //   for (let key of Object.keys(error.response.data)) {
          //     message.error(`${key}: ${error.response.data[key]}`);
          //   }
          // } else {
          //   message.error("오류가 발생했습니다.");
          // }
        });
    } else {
      userAPI
        .createUser({
          ...values,
        })
        .then((res) => {
          if (res.data.linusCode === 1) {
            message.success("성공적으로 생성되었습니다.");
            navigate(`/user/user`);
          } else {
            message.error(res.data.data.msg);
          }
        })
        .catch((error) => {
          if (error.response.data.detail) {
            message.error(error.response.data.detail);
          } else if (error.response.status < 500) {
            for (let key of Object.keys(error.response.data)) {
              message.error(`${key}: ${error.response.data[key]}`);
            }
          } else {
            message.error("오류가 발생했습니다.");
          }
        });
    }
  };

  const [writeForm] = Form.useForm();

  const onFinishWrite = (values) => {
    const data = {
      ...values,
      target: '회원',
      userEmail: user?.email,
      point: values?.increase ? values.point : -values.point
    }

    orderAPI.createPointHistory(data).then(res => {
      if (res.data.linusCode === 1) {
        message.success("올바르게 처리 되었습니다.");
        fetchUser()
      }
    }).catch((error) => {
      if (error?.response?.data?.data?.msg) {
        alert(error.response.data.data.msg);
      }
    });
  }

  const paymentColumns = [
    {
      title: "주문번호",
      dataIndex: 'order',
      width: "12%",
      render: (val) => {
        return val?.orderNumber
      },
    },
    {
      title: "주문 시각",
      dataIndex: 'createdDate',
      width: '10%',
      render: (val) => {
        return getDateTimeString(new Date(val))
      },
    },
    {
      title: "결제자명/이메일",
      dataIndex: 'user',
      width: '10%',
      render: (val) => {
        return user?.name + '\n' + user?.email
      },
    },
    {
      title: "주문 상품",
      dataIndex: 'order',
      width: "20%",
      render: val => {
        return (
          <div>{val?.orderProducts?.map((item) => `[${item?.product?.group}] ${item?.product?.name} x ${item?.quantity}개 (${numberWithCommas(item?.product?.price * item?.quantity)}원)`).toString()}</div>
        )
      }
    },
    {
      title: "주문 금액",
      dataIndex: 'price',
      render: val => {
        return numberWithCommas(val)
      }
    },
    {
      title: "지불 수단",
      dataIndex: 'type',
      render: val => {
        return val
      }
    },
    {
      title: "주문 상태",
      width: "10%",
      dataIndex: 'order',
      render: val => {
        return (val?.status)
      }
    },
    {
      title: "환불처리",
      dataIndex: "isUsedMoreOnce",
      width: "10%",
      render: (val, obj) => {
        return (
          (!val && obj?.order?.status !== '환불완료') &&
          <Button
            danger
            onClick={() => {
              if (window.confirm('해당 주문을 환불 처리 하시겠습니까?')) {
                orderAPI.editOrder({
                  id: obj?.order?.id,
                  status: '환불완료'
                }).then(res => {
                  if (res.data.linusCode === 1) {
                    message.success('환불처리 되었습니다.')
                    fetchUser()
                  }
                })
              }

            }}
          >
            환불처리
          </Button>
        )
      },
    },
  ];

  const open = useDaumPostcodePopup();

  const handleComplete = (data) => {
    let fullAddress = data.address;
    let extraAddress = "";

    if (data.addressType === "R") {
      if (data.bname !== "") {
        extraAddress += data.bname;
      }
      if (data.buildingName !== "") {
        extraAddress +=
          extraAddress !== "" ? `, ${data.buildingName}` : data.buildingName;
      }
      fullAddress += extraAddress !== "" ? ` (${extraAddress})` : "";
    }

    form.setFieldValue("address", fullAddress);
    form.setFieldValue("zipCode", data.zonecode);

    // console.log(fullAddress); // e.g. '서울 성동구 왕십리로2길 20 (성수동1가)'
  };

  const handleClick = () => {
    open({ onComplete: handleComplete });
  };

  const sizeOptions = ["11.9", "12.9", "13.0", "13.2", "13.5", "13.8", "14.0"];
  const colorOptions = [
    "브라운",
    "핑크",
    "초코",
    "블랙",
    "헤이즐",
    "그레이",
    "블루",
    "그린",
  ];

  const deleteUser = (id) => {
    if (window.confirm('정말 해당 계정을 강제 탈퇴 하시겠습니까? 계정 정보가 모두 삭제되며 복구 할 수 없습니다.')) {
      userAPI.deleteUsers({selectedItems :[id]}).then((res) => {
        if (res.data.linusCode === 1) {
          message.success('강제 탈퇴 처리 되었습니다.')
          navigate(`/user/user`);
        }
      })
    }
  }

  const certColumns = [
    { title: "ID", dataIndex: "id" },
    {
      title: "수료과정",
      dataIndex: "certificateType",
      render: (val) => {
        return val?.name ?? '-'
      },
    },
    {
      title: "수료일",
      dataIndex: "date",
      render: (val) => {
        return val ? val?.split('T')[0] : '-'
      },
    },
    {
      title: "수료번호",
      dataIndex: "completeNumber",
      render: (val) => {
        return val ?? '-'
      },
    },
    {
      title: "자격번호",
      dataIndex: "certNumber",
      render: (val) => {
        return val ?? '-'
      },
    },
    {
      title: "생성일",
      dataIndex: "createdDate",
      width: "10%",
      render: (val) => {
        return dateToString(val);
      },
    },
    {
      title: "업데이트 시각",
      dataIndex: "updatedDate",
      width: "10%",
      render: (val) => {
        return dateToString(val);
      },
    },
    {
      title: '승인 처리',
      render: obj => {
        return (
          obj?.isConfirm ?
            <div>
              승인됨
              <Button
                style={{marginLeft: 5}}
                danger
                onClick={e => {
                  e.preventDefault()
                  if (window.confirm('승인 취소처리 하시겠습니까?')) {
                    userAPI.editCertificates({
                      id: obj?.id,
                      isConfirm: false
                    }).then(res => {
                      fetchUser()
                    })
                  }
                }}>
                승인 취소
              </Button>
            </div>
            :
            <Button onClick={e => {
              e.preventDefault()
              if (window.confirm('승인처리 하시겠습니까?')) {
                userAPI.editCertificates({
                  id: obj?.id,
                  isConfirm: true
                }).then(res => {
                  fetchUser()
                })
              }
            }}>
              승인
            </Button>
        )
      }
    },
    {
      title: '삭제',
      render: obj => {
        return ( !obj?.isConfirm &&
          <div>
            <Button
              style={{marginLeft: 5}}
              danger
              onClick={e => {
                e.preventDefault()
                if (window.confirm('삭제 하시겠습니까?')) {
                  userAPI.deleteCertificates({
                    id: obj?.id,
                  }).then(res => {
                    fetchUser()
                  })
                }
              }}>
              삭제
            </Button>
          </div>
        )
      }
    }
  ];

  return (
    <>
      <div>
        <PageHeader
          className="site-page-header"
          title={`사용자 정보 ${isEdit ? "수정" : "추가"}`}
          subTitle={`작성 후 저장 버튼을 눌러주세요.`}
        />
        <Divider />
        {loading && <></>}
      </div>
      {!loading && (
        <>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Form
              form={form}
              name="register"
              onFinish={onFinish}
              initialValues={user}
              scrollToFirstError
              style={{ width: "50%" }}
            >
              {/* <Form.Item name="type" label="유형">
                <Select placeholder="유형을 선택해주세요.">
                  {[
                    "최상단 문구",
                    "필독 사용자사항",
                    "배송 관련",
                    "취소/교환/반품",
                    "결제 관련",
                  ].map((c) => {
                    return (
                      <Select.Option key={c} value={c}>
                        {c}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item> */}

              <Form.Item name="name" required label="이름">
                <Input placeholder="이름을 입력해주세요." />
              </Form.Item>

              <Form.Item name="phone" required label="휴대폰 번호">
                <Input placeholder="연락처를 입력해주세요." />
              </Form.Item>

              <Form.Item name="email" required label="이메일">
                <Input placeholder="이메일 주소를 입력해주세요." />
              </Form.Item>

              <Form.Item name="gender" label="성별">
                <Input placeholder="성별을 입력해주세요." />
              </Form.Item>

              <Form.Item name="birth" label="생년월일">
                <Input placeholder="생년월일을 입력해주세요." />
              </Form.Item>

              <Form.Item name="receiptEmail" label="수신 이메일">
                <Input placeholder="수신 이메일을 입력해주세요." />
              </Form.Item>

              <Form.Item name="isExpert" label="회원 타입 구분">
                <Select placeholder="회원 타입 구분">
                  {[
                    { label: "전체", value: null },
                    { label: "전문가", value: true },
                    { label: "일반 회원", value: false },
                  ].map((c) => {
                    return (
                      <Select.Option key={c.label} value={c.value}>
                        {c.label}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>

              <Form.Item name="coin1" label="부여할 코인 수 (심리유형 개인)">
                <Input placeholder="부여할 코인 수를 입력해 주세요." />
              </Form.Item>

              <Form.Item name="coin2" label="부여할 코인 수 (심리유형 단체)">
                <Input placeholder="부여할 코인 수를 입력해 주세요." />
              </Form.Item>

              <Form.Item name="coin3" label="부여할 코인 수 (도형심리 개인)">
                <Input placeholder="부여할 코인 수를 입력해 주세요." />
              </Form.Item>

              <Form.Item name="coin4" label="부여할 코인 수 (도형심리 단체)">
                <Input placeholder="부여할 코인 수를 입력해 주세요." />
              </Form.Item>

              <Form.Item name="remainCash" label="남은 코인 잔여량 (합산)">
                <Input disabled placeholder="" />
              </Form.Item>

              <Form.Item name="memo" label="메모">
                <TextArea placeholder="" />
              </Form.Item>

              {/*{user?.certificate?.id ?*/}
              {/*  <div style={{marginBottom: 50, width: '100%'}}>*/}
              {/*    <Descriptions*/}
              {/*      labelStyle={{ fontWeight: 800, marginBottom: 30 }}*/}
              {/*      bordered*/}
              {/*    >*/}
              {/*      <Descriptions.Item label="수료 과정" span={2}>*/}
              {/*        {user.certificate?.name ?? "-"}*/}
              {/*      </Descriptions.Item>*/}
              {/*      <Descriptions.Item label="수료일" span={2}>*/}
              {/*        {onlyDateToString(user.certificate?.date) ?? "-"}*/}
              {/*      </Descriptions.Item>*/}
              {/*      <Descriptions.Item label="수료 번호" span={2}>*/}
              {/*        {user.certificate?.completeNumber ?? "-"}*/}
              {/*      </Descriptions.Item>*/}
              {/*      <Descriptions.Item label="자격 번호" span={2}>*/}
              {/*        {user.certificate?.certNumber ?? "-"}*/}
              {/*      </Descriptions.Item>*/}
              {/*      <Descriptions.Item label="승인 여부" span={1}>*/}
              {/*        {user.certificate?.isConfirm ? "승인" : '미승인'}*/}
              {/*      </Descriptions.Item>*/}
              {/*    </Descriptions>*/}
              {/*  </div>*/}
              {/*  :*/}
              {/*  null*/}
              {/*}*/}

              {/*<Form.Item name="password" label="비밀번호 변경">*/}
              {/*  <Input placeholder="비밀번호를 입력해주세요." type={'password'} />*/}
              {/*</Form.Item>*/}

              <Form.Item>
                <div style={{ display: "flex", justifyContent: "end" }}>
                  <Button
                    type="danger"
                    htmlType="submit"
                    onClick={e => {
                      e.preventDefault()
                      deleteUser(user?.id)
                    }}
                  >
                    강제 탈퇴
                  </Button>
                  <Button
                    htmlType="submit"
                    icon={<CloseOutlined />}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/user/user`);
                    }}
                    style={{ margin: "0 10px" }}
                  >
                    목록
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<CheckOutlined />}
                  >
                    저장
                  </Button>
                </div>
              </Form.Item>
            </Form>
          </div>
          <div>
            <h3>자격 목록</h3>

            <Table
              pagination={false}
              loading={loading}
              columns={certColumns}
              dataSource={user?.certificates}
              // rowSelection={{
              //   type: "checkbox",
              //   ...rowSelection,
              // }}
            />
          </div>
          <div style={{marginTop: 10}}>
            <h3>주문 목록</h3>

            <Table
              pagination={false}
              loading={loading}
              columns={paymentColumns}
              dataSource={user?.payments}
              // rowSelection={{
              //   type: "checkbox",
              //   ...rowSelection,
              // }}
            />
          </div>
        </>
      )}
    </>
  );
}

export default UserForm;
